// client/src/components/ClientDashboard.js

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { firestore, storage } from '../../firebase';
import { doc, updateDoc, collection, getDocs, onSnapshot, query, limit as fsLimit } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ref, listAll, list, getMetadata } from 'firebase/storage';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import ClientNavbar from './ClientNavbar';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';

const CACHE_TTL_MS = 5 * 60 * 1000;     // 5 minutes
const COMPANY_TTL_MS = 60 * 60 * 1000;  // 1 heure (cache UID entreprise)
const PROBE_COOLDOWN_MS = 30 * 1000;    // 30s : évite de spammer Storage au focus
const REVALIDATE_AGE_MS = 60 * 1000;    // si cache a > 60s → on tente une réval silencieuse
const ENTREPRISE_LIST_LIMIT = 50;       // Firestore rules: list requires limit <= 50
const dashCacheKey = (uid) => `cache:dashIndex:${uid}`;
const nowMs = () => Date.now();

const ClientDashboard = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);
  const [newPseudo, setNewPseudo] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [photos, setPhotos] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [invitations, setInvitations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [count, setCount] = useState(0); // Factures perso
  const [countInMetadata, setCountInMetadata] = useState(0); // Factures pro
  // eslint-disable-next-line no-unused-vars
  const [employeesStats, setEmployeesStats] = useState([]);
  const [userId, setUserId] = useState(null);
  const auth = getAuth();

  // Garde-fous révalidation
  const isRefreshingRef = useRef(false);
  const lastProbeAtRef = useRef(0);

  // -----------------------------
  // Utils cache localStorage
  // -----------------------------
  const lsGet = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };
  const lsSet = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  };

  // -----------------------------
  // Helpers index (sérialisation minimale)
  // -----------------------------
  const serializePayload = (payload = {}) => ({
    photos: (payload.photos || []).map((p) => ({
      name: p.name || '',
      timestamp: p.timestamp || null,
      isPro: !!p.isPro,
      isPerso: !!p.isPerso,
    })),
    persoCount: payload.persoCount || 0,
    proCount: payload.proCount || 0,
    latestMs: payload.latestMs || 0,
  });

  const withDisplayDates = (payload = {}) => {
    const photos = (payload.photos || []).map((p) => ({
      ...p,
      displayDate: p.timestamp
        ? new Date(p.timestamp).toLocaleString('fr-FR', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : 'Date inconnue',
    }));
    return { ...payload, photos };
  };

  const deserializePayload = (raw) => {
    if (!raw || typeof raw !== 'object') return null;
    return withDisplayDates({
      photos: Array.isArray(raw.photos)
        ? raw.photos.map((p) => ({
            ...p,
            timestamp: typeof p.timestamp === 'number' ? p.timestamp : null,
          }))
        : [],
      persoCount: raw.persoCount || 0,
      proCount: raw.proCount || 0,
      latestMs: raw.latestMs || 0,
    });
  };

  const loadDashCache = (uid) => {
    if (!uid) return null;
    try {
      const raw = localStorage.getItem(dashCacheKey(uid));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const ts = parsed?.ts || 0;
      if (!ts || nowMs() - ts > CACHE_TTL_MS) return null;
      const payload = deserializePayload(parsed.payload);
      if (!payload) return null;
      return { ...payload, _ts: ts };
    } catch {
      return null;
    }
  };

  const saveDashCache = (uid, payload) => {
    if (!uid || !payload) return;
    try {
      localStorage.setItem(
        dashCacheKey(uid),
        JSON.stringify({ ts: nowMs(), payload: serializePayload(payload) })
      );
    } catch {
      // cache best-effort
    }
  };

  // --------------------------------------------------
  // getCurrentCompanyUid (avec cache)
  // --------------------------------------------------
  const getCurrentCompanyUid = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.isAnonymous) return null;

    const userEmail = currentUser.email;
    if (!userEmail) return null;

    const cacheKey = `cache:companyUid:${userEmail}`;
    const cached = lsGet(cacheKey);
    const now = Date.now();
    if (cached && now - cached.ts < COMPANY_TTL_MS) {
      return cached.companyId;
    }

    // Scan (schéma invitations = tableau)
    const entrepriseRef = collection(firestore, 'entreprises');
    const querySnapshot = await getDocs(query(entrepriseRef, fsLimit(ENTREPRISE_LIST_LIMIT)));
    let foundCompanyId = null;

    for (const docSnap of querySnapshot.docs) {
      const data = docSnap.data();
      if (data.invitations && Array.isArray(data.invitations)) {
        const accepted = data.invitations.find(
          (inv) => inv.employeeEmail === userEmail && inv.status === 'accepted'
        );
        if (accepted) {
          foundCompanyId = data.userId || null;
          break;
        }
      }
    }

    lsSet(cacheKey, { ts: now, companyId: foundCompanyId });
    return foundCompanyId;
  }, [auth]);

  // --------------------------------------------------
  // fetchInvitations (avec cache léger + SWR silencieux)
  // --------------------------------------------------
  const fetchInvitations = useCallback(
    async (email) => {
      if (!email) return;

      const cacheKey = `cache:invitations:${email}`;
      const cached = lsGet(cacheKey);
      const now = Date.now();

      if (cached && now - cached.ts < CACHE_TTL_MS) {
        setInvitations(cached.data || []);
        // Révalidation en arrière-plan (non bloquante)
        void (async () => {
          const fresh = await fetchInvitationsCore(email);
          if (fresh.changed) {
            setInvitations(fresh.items);
            lsSet(cacheKey, { ts: Date.now(), data: fresh.items });
          }
        })();
        return;
      }

      const fresh = await fetchInvitationsCore(email);
      setInvitations(fresh.items);
      lsSet(cacheKey, { ts: now, data: fresh.items });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const fetchInvitationsCore = async (email) => {
    const entrepriseRef = collection(firestore, 'entreprises');
    const querySnapshot = await getDocs(query(entrepriseRef, fsLimit(ENTREPRISE_LIST_LIMIT)));
    const fetched = [];
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.invitations && Array.isArray(data.invitations)) {
        const matching = data.invitations.filter(
          (invitation) =>
            invitation.employeeEmail === email && invitation.status === 'pending'
        );
        matching.forEach((invitation) => {
          fetched.push({
            id: docSnap.id,
            ...invitation,
          });
        });
      }
    });
    return { items: fetched, changed: true };
  };

  // --------------------------------------------------
  // fetchStorageStats — passe Storage (listAll + metadata par item)
  // + cache SWR (5 min)
  // + cheap probe (list 1 item) pour éviter la passe complète
  // --------------------------------------------------
  const fetchStorageStats = useCallback(
    async ({ force = false } = {}) => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const uid = currentUser.uid;
      const now = nowMs();

      // 1) Cache localStorage
      const cachedDash = !force ? loadDashCache(uid) : null;
      if (cachedDash) {
        applyStorageStateFromCache(cachedDash);
        if (
          now - (cachedDash._ts || 0) > REVALIDATE_AGE_MS &&
          !isRefreshingRef.current &&
          navigator.onLine
        ) {
          isRefreshingRef.current = true;
          void revalidateStorage(uid, cachedDash).finally(() => {
            isRefreshingRef.current = false;
          });
        }
        return;
      }

      // 2) Full compute (ou force)
      const payload = await computeStoragePayload(uid);
      applyStorageStateFromCache(payload);
      saveDashCache(uid, payload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth]
  );

  // Cheap probe: liste 1 seul item et compare sa date de création à la dernière connue
  const cheapProbeHasNewer = async (uid, cachedLatestMs) => {
    try {
      const storageRef = ref(storage, `${uid}/photos`);
      const firstPage = await list(storageRef, { maxResults: 1 });
      if (!firstPage.items.length) return false;
      const meta = await getMetadata(firstPage.items[0]);
      const latestCandidateMs = meta?.timeCreated ? new Date(meta.timeCreated).getTime() : 0;
      return latestCandidateMs > (cachedLatestMs || 0);
    } catch {
      // En cas d’erreur réseau/perms → on ne déclenche pas de full pass forcée
      return false;
    }
  };

  const revalidateStorage = async (uid, oldPayload) => {
    const newer = await cheapProbeHasNewer(uid, oldPayload?.latestMs || 0);
    if (!newer) {
      saveDashCache(uid, oldPayload);
      return;
    }
    const freshPayload = await computeStoragePayload(uid);
    applyStorageStateFromCache(freshPayload);
    saveDashCache(uid, freshPayload);
  };

  const computeStoragePayload = async (uid) => {
    // On récupère l'UID de l’entreprise (cache 1h)
    const companyId = await getCurrentCompanyUid();

    // Liste du dossier utilisateur
    const storageRef = ref(storage, `${uid}/photos`);
    const res = await listAll(storageRef);

    let persoCount = 0;
    let proCount = 0;
    const minimalPhotos = [];

    await Promise.all(
      res.items.map(async (item) => {
        try {
          const metadata = await getMetadata(item);
          const createdAt = metadata?.timeCreated ? new Date(metadata.timeCreated) : null;
          const customCategory = metadata?.customMetadata?.category || 'unknown';

          const isPerso = customCategory === 'perso';
          const isPro = (companyId && customCategory === companyId) || customCategory === 'pro';

          if (isPerso) persoCount += 1;
          if (isPro) proCount += 1;

          minimalPhotos.push({
            name: item.name,
            timestamp: createdAt ? createdAt.getTime() : null,
            displayDate: createdAt
              ? createdAt.toLocaleString('fr-FR', {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'Date inconnue',
            isPro,
            isPerso,
          });
        } catch {
          // ignore item si metadata inaccessible
        }
      })
    );

    minimalPhotos.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    const latestMs = minimalPhotos[0]?.timestamp || 0;

    return {
      photos: minimalPhotos,
      persoCount,
      proCount,
      latestMs,
    };
  };

  const applyStorageStateFromCache = (payload) => {
    const normalized = withDisplayDates(payload);
    setPhotos(normalized.photos || []);
    setCount(normalized.persoCount || 0);
    setCountInMetadata(normalized.proCount || 0);
    // Legacy caches (utilisés ailleurs)
    localStorage.setItem('facturesCount', String(normalized.persoCount || 0));
    localStorage.setItem('facturesCountMetadata', String(normalized.proCount || 0));
  };

  // --------------------------------------------------
  // handleSavePseudo : enregistrement du pseudo (robuste)
  // --------------------------------------------------
  const handleSavePseudo = async () => {
    const uid = auth.currentUser?.uid;
    const clean = (newPseudo || '').trim();
    if (!uid || !clean) {
      alert('Veuillez entrer un pseudo valide.');
      return;
    }
    try {
      const userRef = doc(firestore, 'users', uid);
      await updateDoc(userRef, { pseudo: clean });
      setUser((prev) => ({ ...(prev || {}), pseudo: clean }));
      setShowModal(false);
      setNewPseudo('');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Erreur lors de la mise à jour du pseudo :', e);
      alert('Impossible d’enregistrer le pseudo. Réessayez plus tard.');
    }
  };

  // --------------------------------------------------
  // Auth + user doc (on garde onSnapshot)
  // Hydratation via cache + révalidation ciblée
  // --------------------------------------------------
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const uid = currentUser.uid;
        if (!uid) return;

        setUserId(uid);
        const userDocRef = doc(firestore, 'users', uid);

        const unsubscribeUser = onSnapshot(userDocRef, async (docSnapshot) => {
          if (!docSnapshot.exists()) {
            // eslint-disable-next-line no-console
            console.error('Le document utilisateur n’existe pas pour uid:', uid);
            return;
          }
          const userData = docSnapshot.data();
          setUser({ ...userData, pseudo: userData?.pseudo ?? '' });

          if (!currentUser.isAnonymous) {
            if (userData?.email) {
              fetchInvitations(userData.email);
            }
          }

          await fetchStorageStats({ force: false });
        });

        return () => unsubscribeUser();
      } else {
        navigate('/');
      }
    });

    return () => unsubscribeAuth();
  }, [navigate, auth, fetchInvitations, fetchStorageStats]);

  // --------------------------------------------------
  // Lecture cache des compteurs au montage (instantané)
  // --------------------------------------------------
  useEffect(() => {
    if (!userId) return;
    const cachedCount = localStorage.getItem('facturesCount');
    const cachedCountMetadata = localStorage.getItem('facturesCountMetadata');
    if (cachedCount) setCount(parseInt(cachedCount, 10));
    if (cachedCountMetadata) setCountInMetadata(parseInt(cachedCountMetadata, 10));
  }, [userId]);

  // --------------------------------------------------
  // Rafraîchissement léger: focus/visibilité (throttle + cache-aware)
  // --------------------------------------------------
  useEffect(() => {
    const tryProbe = () => {
      const now = Date.now();
      if (now - lastProbeAtRef.current < PROBE_COOLDOWN_MS) return;
      lastProbeAtRef.current = now;
      // Pas de force: respecte TTL + cheap probe interne
      fetchStorageStats({ force: false });
    };

    const onFocus = () => {
      if (!navigator.onLine) return;
      tryProbe();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        tryProbe();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchStorageStats]);

  // ===========================
  //   STYLE COMPACT (moins haut)
  // ===========================
  const pageStyles = `
.dashboard-container.client-dashboard{align-items:stretch;}

.client-dashboard-shell{width:100%;display:flex;justify-content:center;}
.client-dashboard-inner{width:100%;display:grid;gap:20px;}
.client-dashboard-inner .eb-page-header{margin-bottom:0;}
.client-dashboard-inner .eb-page-title{text-align:left;}

.client-dashboard-hero{display:grid;gap:20px;}
.client-dashboard-heroContent{display:grid;gap:20px;}
.client-dashboard-chip{display:inline-flex;align-items:center;padding:3px 10px;border-radius:var(--eb-radius-pill);font-size:12.5px;font-weight:600;background:var(--eb-brand-50);color:var(--eb-brand);border:1px solid var(--eb-brand-100);width:max-content;}

/* Indicateurs */
.client-dashboard-heroMetrics{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:16px;}
.client-dashboard-metric{
  background:var(--eb-surface);
  border-radius:var(--eb-radius-lg);
  padding:20px;
  border:1px solid var(--eb-border);
  box-shadow:var(--eb-shadow-sm);
  display:grid;gap:6px;align-content:start;min-width:0;
}
.client-dashboard-metric span{font-size:13px;font-weight:600;color:var(--eb-muted);}
.client-dashboard-metric strong{font-size:30px;font-weight:700;letter-spacing:-0.02em;color:var(--eb-text);line-height:1.1;font-variant-numeric:tabular-nums;}
.client-dashboard-metric small{font-size:13px;color:var(--eb-muted);display:flex;align-items:center;gap:6px;}
.client-dashboard-metric small.client-dashboard-trendUp{color:var(--eb-success);font-weight:600;}
.client-dashboard-metric small.client-dashboard-trendDown{color:var(--eb-danger);font-weight:600;}

/* Répartition */
.client-dashboard-distribution{
  background:var(--eb-surface);
  border-radius:var(--eb-radius-lg);
  padding:24px;
  border:1px solid var(--eb-border);
  box-shadow:var(--eb-shadow-sm);
  display:grid;gap:16px;
}
.client-dashboard-distributionHeader{display:grid;gap:4px;}
.client-dashboard-distributionHeader h2{margin:0;font-size:18px;font-weight:700;color:var(--eb-text);line-height:1.25;}
.client-dashboard-distributionHeader p{margin:0;font-size:14px;color:var(--eb-muted);}

.client-dashboard-progressGroup{display:grid;gap:14px;}
.client-dashboard-progressLine{display:grid;gap:6px;}
.client-dashboard-progressHeader{display:flex;align-items:center;justify-content:space-between;font-size:14px;color:var(--eb-text-2);font-weight:600;font-variant-numeric:tabular-nums;}
.client-dashboard-progressBar{width:100%;height:10px;border-radius:999px;background:var(--eb-surface-2);border:1px solid var(--eb-border);overflow:hidden;}
.client-dashboard-progressFill{height:100%;border-radius:999px;transition:width .25s ease;}

/* Cartes secondaires */
.client-dashboard-grid{display:grid;gap:16px;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));align-items:start;}
.client-dashboard-card{background:var(--eb-surface);border-radius:var(--eb-radius-lg);padding:24px;border:1px solid var(--eb-border);box-shadow:var(--eb-shadow-sm);display:grid;gap:14px;position:relative;}
.client-dashboard-card h2{margin:0;font-size:18px;font-weight:700;color:var(--eb-text);line-height:1.25;}
.client-dashboard-card p{margin:0;font-size:14px;color:var(--eb-muted);line-height:1.45;}

/* Listes & actions */
.client-dashboard-list{display:grid;gap:8px;margin:0;padding:0;list-style:none;}
.client-dashboard-listItem{display:flex;flex-direction:column;gap:2px;background:var(--eb-surface-2);border-radius:var(--eb-radius);padding:12px 14px;border:1px solid var(--eb-border);}
.client-dashboard-listItem strong{font-size:14px;color:var(--eb-text);display:flex;align-items:center;gap:8px;}
.client-dashboard-listItem span{font-size:13px;color:var(--eb-muted);}
.client-dashboard-empty{margin:0;padding:24px 16px;text-align:center;font-size:14px;color:var(--eb-muted);background:var(--eb-surface-2);border:1px dashed var(--eb-border-strong);border-radius:var(--eb-radius);}
.client-dashboard-actions{display:flex;flex-wrap:wrap;gap:12px;}
.client-dashboard-actions:empty{display:none;}
.client-dashboard-actionButton{display:inline-flex;align-items:center;gap:6px;padding:4px 12px;border-radius:var(--eb-radius-pill);font-size:13px;font-weight:600;background:var(--eb-warning-bg);color:var(--eb-warning);border:1px solid #fde3b0;text-decoration:none;}

/* Modale pseudo */
.client-dashboard-overlay{position:fixed;inset:0;z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(15,35,56,.55);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);}
.client-dashboard-modal{position:relative;width:100%;max-width:420px;max-height:calc(100dvh - 32px);overflow-y:auto;background:var(--eb-surface);border-radius:var(--eb-radius-xl);box-shadow:var(--eb-shadow-lg);padding:24px;display:flex;flex-direction:column;gap:14px;text-align:left;}
.client-dashboard-modal h2{margin:0;padding-right:44px;font-size:20px;font-weight:700;text-align:left;color:var(--eb-text);}
.client-dashboard-modal__close{position:absolute;top:14px;right:14px;width:40px;height:40px;display:inline-flex;align-items:center;justify-content:center;border:0;border-radius:var(--eb-radius-sm);background:transparent;color:var(--eb-muted);font-size:26px;line-height:1;cursor:pointer;}
.client-dashboard-modal__close:hover{background:var(--eb-surface-2);color:var(--eb-text);}
.client-dashboard-modal__actions{display:flex;justify-content: center;}

@media (max-width:480px){
  .client-dashboard-heroMetrics{grid-template-columns:1fr 1fr;gap:10px;}
  .client-dashboard-metric{padding:14px;border-radius:var(--eb-radius);}
  .client-dashboard-metric strong{font-size:24px;}
  .client-dashboard-metric small{font-size:12.5px;}
  .client-dashboard-distribution{padding:20px 16px;}
  .client-dashboard-overlay{align-items:flex-end;padding:0;}
  .client-dashboard-modal{max-width:none;border-radius:var(--eb-radius-xl) var(--eb-radius-xl) 0 0;padding:22px 20px calc(20px + env(safe-area-inset-bottom,0px));}
  .client-dashboard-modal__actions .eb-btn{width:100%;}
}
`;

  const formatNumber = (value) => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value.toLocaleString('fr-FR');
    }
    return value ?? '0';
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return null;
    const diffMs = Date.now() - timestamp;
    if (diffMs < 0) return "À l'instant";
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Il y a ${hours} h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `Il y a ${days} j`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `Il y a ${weeks} sem`;
    const months = Math.floor(days / 30);
    if (months < 12) return `Il y a ${months} mois`;
    const years = Math.floor(days / 365);
    return `Il y a ${years} an${years > 1 ? 's' : ''}`;
  };

  const statsFromPhotos = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const startOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
    const weekAgo = now.getTime() - 7 * 24 * 60 * 60 * 1000;

    let monthDocs = 0;
    let previousMonthDocs = 0;
    let lastSevenDays = 0;
    photos.forEach((photo) => {
      if (photo?.timestamp) {
        if (photo.timestamp >= startOfMonth) monthDocs += 1;
        if (photo.timestamp >= startOfPreviousMonth && photo.timestamp < startOfMonth) previousMonthDocs += 1;
        if (photo.timestamp >= weekAgo) lastSevenDays += 1;
      }
    });

    const monthTrend = previousMonthDocs > 0
      ? ((monthDocs - previousMonthDocs) / previousMonthDocs) * 100
      : null;
    const hasNewActivity = previousMonthDocs === 0 && monthDocs > 0;

    return {
      monthDocs,
      previousMonthDocs,
      lastSevenDays,
      monthTrend,
      hasNewActivity,
    };
  }, [photos]);

  const totalFactures = (count || 0) + (countInMetadata || 0);
  const completionRate = totalFactures > 0 ? Math.round((countInMetadata / totalFactures) * 100) : 0;
  const lastUpload = photos.find((photo) => photo?.timestamp);
  const lastUploadRelative = formatRelativeTime(lastUpload?.timestamp);
  const pendingInvitations = invitations.length;
  const { monthDocs, lastSevenDays, monthTrend, hasNewActivity } = statsFromPhotos;

  const heroMetrics = useMemo(() => {
    const metrics = [];

    metrics.push({
      label: 'Total factures',
      value: totalFactures,
      trend: hasNewActivity ? null : monthTrend,
      info: monthDocs ? `${monthDocs} ce mois-ci` : null,
      secondaryInfo: hasNewActivity ? 'Nouvelle activité ce mois-ci' : null,
    });

    metrics.push({
      label: 'Factures pro',
      value: countInMetadata,
      info: totalFactures > 0 ? `Part pro ${completionRate}%` : 'En attente de vos premiers envois',
    });

    metrics.push({
      label: 'Activité 7 jours',
      value: lastSevenDays,
      info: lastUploadRelative || 'Aucune facture récente',
    });

    if (pendingInvitations > 0) {
      metrics.push({
        label: 'Invitations en attente',
        value: pendingInvitations,
        info: 'Répondez pour rejoindre votre équipe',
      });
    }

    return metrics;
  }, [totalFactures, monthTrend, monthDocs, hasNewActivity, countInMetadata, completionRate, lastSevenDays, lastUploadRelative, pendingInvitations]);

  const distributionSegments = useMemo(() => {
    const segments = [
      { label: 'Factures perso', value: count || 0, color: 'var(--eb-brand)' },
      { label: 'Factures pro', value: countInMetadata || 0, color: 'var(--eb-brand-300)' },
    ];
    return segments.filter((segment) => segment.value > 0);
    // eslint-disable-next-line
  }, [count, countInMetadata, photos]);

  const distributionTotal = distributionSegments.reduce((sum, segment) => sum + segment.value, 0);

  return (
    <>
      <style>{pageStyles}</style>
      <div className="dashboard-container client-dashboard">
        <ClientNavbar />
        <div className="client-dashboard-shell">
          <div className="client-dashboard-inner">
            <header className="eb-page-header">
              <div>
                <h1 className="eb-page-title">Vos statistiques Ecobill</h1>
              </div>
            </header>
            <section className="client-dashboard-hero">
              <div className="client-dashboard-heroContent">
                <div className="client-dashboard-heroMetrics">
                  {heroMetrics.map((metric, index) => {
                    const trendClass =
                      metric.trend !== undefined && metric.trend !== null
                        ? metric.trend >= 0
                          ? 'client-dashboard-trendUp'
                          : 'client-dashboard-trendDown'
                        : '';
                    const trendIcon =
                      metric.trend !== undefined && metric.trend !== null
                        ? metric.trend >= 0
                          ? <FaArrowUp size={12} />
                          : <FaArrowDown size={12} />
                        : null;

                    const trendText =
                      metric.trend !== undefined && metric.trend !== null
                        ? `${Math.abs(metric.trend).toFixed(1)}% vs mois dernier`
                        : null;

                    return (
                      <div key={`${metric.label}-${index}`} className="client-dashboard-metric">
                        <span>{metric.label}</span>
                        <strong>{formatNumber(metric.value)}</strong>
                        {trendText && (
                          <small className={trendClass}>
                            {trendIcon}
                            {trendText}
                          </small>
                        )}
                        {metric.info && <small>{metric.info}</small>}
                        {metric.secondaryInfo && <small>{metric.secondaryInfo}</small>}
                      </div>
                    );
                  })}
                </div>
                <div className="client-dashboard-distribution">
                  <div className="client-dashboard-distributionHeader">
                    <h2>Répartition des factures</h2>
                    <p>
                      {distributionTotal > 0
                        ? 'Visualisez le poids de chaque type de facture scannée.'
                        : 'Ajoutez vos premières factures pour afficher la répartition.'}
                    </p>
                  </div>
                  {distributionTotal > 0 ? (
                    <div className="client-dashboard-progressGroup">
                      {distributionSegments.map((segment) => (
                        <div key={segment.label} className="client-dashboard-progressLine">
                          <div className="client-dashboard-progressHeader">
                            <span>{segment.label}</span>
                            <span>{formatNumber(segment.value)}</span>
                          </div>
                          <div className="client-dashboard-progressBar">
                            <div
                              className="client-dashboard-progressFill"
                              style={{
                                width: `${Math.round((segment.value / distributionTotal) * 100)}%`,
                                background: segment.color,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="client-dashboard-empty">Aucune facture scannée pour l'instant.</p>
                  )}
                  <div className="client-dashboard-actions">
                    {pendingInvitations > 0 && (
                      <div className="client-dashboard-actionButton">
                        {pendingInvitations} invitation{pendingInvitations > 1 ? 's' : ''} à valider
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="client-dashboard-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pseudo-modal-title"
          onClick={() => setShowModal(false)}
        >
          <div
            className="client-dashboard-modal eb-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="client-dashboard-modal__close"
              aria-label="Fermer la fenêtre"
            >
              ×
            </button>
            <h2 id="pseudo-modal-title">Définir un pseudo</h2>
            <input
              type="text"
              className="eb-input"
              value={newPseudo}
              onChange={(e) => setNewPseudo(e.target.value)}
              placeholder="Votre pseudo"
              aria-label="Votre pseudo"
            />
            <div className="client-dashboard-modal__actions">
              <button type="button" onClick={handleSavePseudo} className="eb-btn eb-btn--primary eb-btn--lg">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientDashboard;
